import React, { useState } from "react";

const UpgradePackageCard = ({
  pkg,
  originalPrice,
  onSelectPackage,
  currentPackageFeatures,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const formatFeatureValue = (key, value) => {
    if (value === -1) return "Unlimited";
    if (value === 0) return "Not included";

    const timeBasedFeatures = {
      stuff: "lifetime",
      players: "lifetime",
      teams: "lifetime",
      events: "per month",
      matches: "per month",
      leagues: "per year",
      competitions: "per year",
    };

    const formattedValue = value.toLocaleString();
    const timeUnit = timeBasedFeatures[key];

    return timeUnit ? `${formattedValue} ${timeUnit}` : formattedValue;
  };

  const getFeatureIcon = (featureKey) => {
    const icons = {
      stuff: "👥",
      players: "🎮",
      teams: "🏆",
      events: "📅",
      matches: "⚽",
      leagues: "🏟️",
      competitions: "🏅",
    };
    return icons[featureKey] || "✓";
  };

  const getFeatureLabel = (featureKey) => {
    const labels = {
      stuff: "Staff Members",
      players: "Players",
      teams: "Teams",
      events: "Events",
      matches: "Matches",
      leagues: "Leagues",
      competitions: "Create Competitions",
    };
    return labels[featureKey] || featureKey;
  };

  const getFeatureComparison = (key, newValue, currentValue) => {
    if (newValue === currentValue) return "same";
    if (newValue === -1 && currentValue !== -1) return "upgrade";
    if (newValue > currentValue) return "upgrade";
    return "same";
  };

  const features = Object.entries(pkg.features);
  const discount = originalPrice ? originalPrice - pkg.price : 0;
  const discountPercentage = originalPrice
    ? Math.round((discount / originalPrice) * 100)
    : 0;

  // Handle upgrade payment process
  const handleUpgradePayment = async () => {
    try {
      setIsProcessing(true);
      setPaymentError(null);

      console.log(
        `⬆️ Initiating upgrade payment for package: ${pkg.name} (${pkg.id})`
      );

      // Call backend payment endpoint (same endpoint handles upgrades automatically)
      const response = await fetch(
        `https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/organization/subscription/pay`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            packageId: pkg.id,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Upgrade payment failed");
      }

      console.log(`✅ Upgrade payment response:`, data);

      // Handle different payment response types
      if (data.success) {
        if (data.data.type === "mock") {
          // Mock payment - upgrade activated immediately
          console.log(`🧪 Mock upgrade completed - subscription upgraded`);

          const upgradeInfo = data.data.upgrade;

          // Show success message with upgrade details
          alert(
            `🎉 Subscription upgraded successfully!\n\nFrom: Current Plan\nTo: ${
              pkg.name
            }\nCredit Applied: ${upgradeInfo.creditApplied.toFixed(
              2
            )} SAR\nAmount Paid: ${upgradeInfo.finalPrice.toFixed(
              2
            )} SAR\n\nMode: Development (Mock Payment)`
          );

          // Refresh page or redirect to dashboard
          if (onSelectPackage) {
            onSelectPackage(pkg, data);
          } else {
            window.location.reload();
          }
        } else if (data.data.type === "zero-amount") {
          // Zero amount upgrade - completed immediately with full credit
          console.log(`💰 Zero-amount upgrade completed - full credit applied`);

          const upgradeInfo = data.data.upgrade;

          // Show success message with upgrade details
          alert(
            `🎉 Subscription upgraded successfully!\n\nFrom: Current Plan\nTo: ${
              pkg.name
            }\nCredit Applied: ${upgradeInfo.creditApplied.toFixed(
              2
            )} SAR\nAmount Paid: ${upgradeInfo.finalPrice.toFixed(
              2
            )} SAR\n\nUpgrade completed with full credit!`
          );

          // Refresh page or redirect to dashboard
          if (onSelectPackage) {
            onSelectPackage(pkg, data);
          } else {
            window.location.reload();
          }
        } else if (data.data.type === "geidea") {
          // Real payment - redirect to payment link
          console.log(
            `🔗 Redirecting to upgrade payment link: ${data.data.paymentLink}`
          );

          const upgradeInfo = data.data.upgrade;

          // Store transaction info for later reference
          localStorage.setItem(
            "pendingTransaction",
            JSON.stringify({
              transactionId: data.data.transaction.transactionId,
              packageId: pkg.id,
              packageName: pkg.name,
              amount: data.data.transaction.amount,
              paymentLink: data.data.paymentLink,
              isUpgrade: true,
              creditApplied: upgradeInfo.creditApplied,
              originalPrice: upgradeInfo.originalPrice,
            })
          );

          // Redirect to payment page
          window.open(data.data.paymentLink, "_blank");

          // Show instructions to user with upgrade details
          alert(
            `💳 Upgrade payment link opened in new tab.\n\nUpgrade to: ${
              pkg.name
            }\nOriginal Price: ${
              upgradeInfo.originalPrice
            } SAR\nCredit Applied: ${upgradeInfo.creditApplied.toFixed(
              2
            )} SAR\nAmount to Pay: ${upgradeInfo.finalPrice.toFixed(
              2
            )} SAR\n\nPlease complete the payment and return to this page.`
          );
          
          // Reload page after user closes the alert
          window.location.reload();

          if (onSelectPackage) {
            onSelectPackage(pkg, data);
          }
        }
      } else {
        throw new Error(
          data.message || "Upgrade payment initialization failed"
        );
      }
    } catch (error) {
      console.error("💥 Upgrade payment error:", error);
      setPaymentError(error.message);

      // Show user-friendly error message
      alert(
        `❌ Upgrade Payment Error\n\n${error.message}\n\nPlease try again or contact support.`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="relative bg-white rounded-2xl shadow-lg border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
      {/* Discount Badge */}
      {discount > 0 && (
        <div className="absolute -top-4 -right-4">
          <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
            Save {discount} SAR ({discountPercentage}%)
          </span>
        </div>
      )}

      <div className="p-8">
        {/* Package Header */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
          <p className="text-gray-600 mb-4">{pkg.description}</p>

          {/* Price */}
          <div className="mb-4">
            {originalPrice && originalPrice !== pkg.price ? (
              <div>
                <div className="text-lg text-gray-500 line-through">
                  {originalPrice} SAR
                </div>
                <div className="flex items-center justify-center">
                  <span className="text-5xl font-bold text-gray-900">
                    {pkg.price}
                  </span>
                  <span className="text-gray-500 ml-2">SAR/ year</span>
                </div>
                <div className="text-green-600 font-semibold">
                  You save {discount} SAR with upgrade credit!
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <span className="text-5xl font-bold text-gray-900">
                  {pkg.price}
                </span>
                <span className="text-gray-500 ml-2">SAR/ year</span>
              </div>
            )}
          </div>

          {/* Duration */}
          <div className="text-sm text-gray-500">
            {pkg.duration} days subscription
          </div>
        </div>

        {/* Features List with Comparison */}
        <div className="space-y-4 mb-8">
          <h4 className="font-semibold text-gray-900 text-lg border-b border-gray-200 pb-2">
            What's Included
          </h4>
          {features.map(([key, value]) => {
            const currentValue = currentPackageFeatures?.[key] || 0;
            const comparison = getFeatureComparison(key, value, currentValue);

            return (
              <div key={key} className="flex items-start justify-between py-2">
                <div className="flex items-start">
                  <span className="mr-3 text-lg mt-0.5">
                    {getFeatureIcon(key)}
                  </span>
                  <span className="text-gray-700 leading-tight">
                    {getFeatureLabel(key)}
                  </span>
                  {comparison === "upgrade" && (
                    <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-semibold">
                      UPGRADE
                    </span>
                  )}
                </div>
                <div className="text-right ml-4">
                  <span
                    className={`font-semibold ${
                      value === 0
                        ? "text-gray-400"
                        : value === -1
                        ? "text-green-600"
                        : ""
                    }`}
                    style={{
                      color:
                        value !== 0 && value !== -1 ? "#DE561F" : undefined,
                    }}
                  >
                    {formatFeatureValue(key, value)}
                  </span>
                  {comparison === "upgrade" && currentValue !== undefined && (
                    <div className="text-xs text-gray-500">
                      (was {formatFeatureValue(key, currentValue)})
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Error Message */}
        {paymentError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm font-medium">
              ❌ {paymentError}
            </p>
          </div>
        )}

        {/* CTA Button */}
        <button
          className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-200 text-white shadow-lg hover:shadow-xl ${
            isProcessing ? "opacity-75 cursor-not-allowed" : ""
          }`}
          style={{
            background: "linear-gradient(to right, #DE561F, #B8441A)",
          }}
          onMouseEnter={(e) => {
            if (!isProcessing) {
              e.target.style.background =
                "linear-gradient(to right, #C54E1C, #A03D17)";
            }
          }}
          onMouseLeave={(e) => {
            if (!isProcessing) {
              e.target.style.background =
                "linear-gradient(to right, #DE561F, #B8441A)";
            }
          }}
          onClick={handleUpgradePayment}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <div className="flex items-center justify-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing Upgrade...
            </div>
          ) : (
            `Upgrade to ${pkg.name}`
          )}
        </button>

        {/* Upgrade Benefits */}
        {discount > 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-green-600 font-medium">
              💰 Instant savings applied from your current subscription
            </p>
          </div>
        )}

        {/* Payment Info */}
        <div className="mt-2 text-center">
          <p className="text-xs text-gray-500">
            🔒 Secure payment powered by Geidea
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradePackageCard;
